import React, {useCallback, useEffect, useState} from "react";
import {checkOutAgent, listAgents, listSites} from "../api/admin";

const REFRESH_MS = 15000;
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], {hour: "numeric", minute: "2-digit"}) : "");

const Stat = ({value, label, tone}) => (
    <div className={`brk-stat${tone ? ` brk-stat--${tone}` : ""}`}>
        <span className="brk-stat__value">{value}</span>
        <span className="brk-stat__label">{label}</span>
    </div>
);

/** Right now: who's checked in where, who's on a tour, buyers waiting, and staffed listings with nobody there. */
const LiveBoard = ({onAuthError}) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null);
    const [updated, setUpdated] = useState(null);

    const load = useCallback(async () => {
        try {
            const [agents, sites] = await Promise.all([listAgents(), listSites()]);
            setData({agents, sites});
            setUpdated(new Date());
            setError("");
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    if (!data) return error ? <div className="brk-notice brk-notice--error" role="alert">{error}</div> : <p className="brk-empty">Loading…</p>;

    const here = data.agents.filter((a) => a.status === "ACTIVE" && a.checkin && !a.checkin.checkedOutAt);
    const onTour = here.filter((a) => a.busy);
    const waiting = data.sites.filter((s) => s.waiting > 0);
    const uncovered = data.sites.filter((s) => s.agents.length > 0 && s.checkedIn === 0 && !s.settings.toursPaused);

    return (
        <div>
            <div className="brk-card__head">
                <span className="brk-hint">Updates every 15 seconds{updated ? ` · last ${updated.toLocaleTimeString()}` : ""}</span>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={load}>Refresh</button>
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            <div className="brk-stats" style={{marginBottom: 20}}>
                <Stat value={here.length} label="Agents checked in" tone={here.length ? "green" : undefined}/>
                <Stat value={onTour.length} label="On a tour now"/>
                <Stat value={waiting.reduce((n, s) => n + s.waiting, 0)} label="Buyers waiting" tone={waiting.length ? "orange" : undefined}/>
                <Stat value={uncovered.length} label="Staffed listings with nobody there" tone={uncovered.length ? "amber" : undefined}/>
            </div>

            <h3 className="brk-strong" style={{margin: "0 0 8px"}}>Checked in now</h3>
            {here.length === 0 ? <p className="brk-empty">Nobody is checked in.</p> : (
                <div className="brk-table-wrap" style={{marginBottom: 20}}>
                    <table className="brk-table">
                        <thead><tr><th>Agent</th><th>Where</th><th>Since</th><th>Status</th><th/></tr></thead>
                        <tbody>
                        {here.map((a) => (
                            <tr key={a.agentId}>
                                <td className="brk-strong">{a.name}</td>
                                <td>{a.checkin.siteName}</td>
                                <td>{clock(a.checkin.checkedInAt)}</td>
                                <td>{a.busy ? <span className="brk-badge brk-badge--orange">On a tour</span> : <span className="brk-badge brk-badge--green">Free</span>}</td>
                                <td>
                                    <button type="button" className="brk-link" disabled={busy === a.agentId}
                                            onClick={async () => {
                                                setBusy(a.agentId);
                                                try { await checkOutAgent(a.agentId); await load(); }
                                                catch (err) { if (!(await onAuthError(err))) setError(err.message); }
                                                finally { setBusy(null); }
                                            }}>Check out</button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {waiting.length > 0 && (
                <>
                    <h3 className="brk-strong" style={{margin: "0 0 8px"}}>Buyers waiting</h3>
                    <ul className="brk-hint" style={{margin: "0 0 20px", paddingLeft: 18}}>
                        {waiting.map((s) => (
                            <li key={s.siteId}><b>{s.name}</b>: {s.waiting} waiting · {s.checkedIn ? `${s.checkedIn} agent(s) there` : "nobody checked in"}</li>
                        ))}
                    </ul>
                </>
            )}

            <h3 className="brk-strong" style={{margin: "0 0 8px"}}>Staffed listings with nobody there</h3>
            {uncovered.length === 0 ? <p className="brk-empty">Every staffed listing has someone checked in.</p> : (
                <ul className="brk-hint" style={{margin: 0, paddingLeft: 18}}>
                    {uncovered.map((s) => <li key={s.siteId}><b>{s.name}</b>: {s.agents.join(", ")}</li>)}
                </ul>
            )}
        </div>
    );
};

export default LiveBoard;
